'use client';
import React from 'react';
import { exams } from '../../../../api/exams';
import { useMutation } from 'react-query';
import toast from 'react-hot-toast';
import { useNavigate } from 'react-router-dom';

type Question = {
  id: string;
  text: string;
  section: { title: string };
  rating_range_from: number;
  rating_range_to: number;
  type: string;
};

type Props = {
  questions: Question[];
  currentIndex: number;
  setCurrentIndex: React.Dispatch<React.SetStateAction<number>>;
};

const Employee = ({ questions, currentIndex, setCurrentIndex }: Props) => {
  const question = questions[currentIndex];
  const navigate = useNavigate();

  const activeAssessment = JSON.parse(
    window.sessionStorage.getItem('active-assessment') || '{}'
  );

  const userId =
    JSON.parse(sessionStorage.getItem('user') || '{}')?.user?.user_id ||
    window.sessionStorage.getItem('user_id');

  const assessmentType = sessionStorage.getItem('assessment-type');

  const [answers, setAnswers] = React.useState<{
    [questionId: string]: {
      rating: number | null;
      explanation: string;
      duration: number;
      startTime: number;
    };
  }>({});

  const [error, setError] = React.useState<string>('');

  const finishSurveyMutation = useMutation(
    assessmentType === 'review'
      ? exams?.gradeSurveyReviewv2
      : exams.gradeAssessmentv2,
    {
      onSuccess: () => {
        toast.remove();
        toast.success(
          `${assessmentType === 'review' ? 'Review' : 'Survey'} submitted successfully`
        );
        navigate(`/candidate/${assessmentType === 'review' ? 'my-review' : 'dashboard'}`);
      },
      onError: () => {
        toast.remove();
        toast.error(`Error Submitting ${assessmentType === 'review' ? 'Review' : 'Survey'}`);
      },
    }
  );

  const updateAnswersMutation = useMutation(
    assessmentType === 'review' ? exams.updateAnswersv2Review : exams.updateAnswersv2,
    {
      onSuccess: () => {
        finishSurveyMutation.mutate({
          userId: assessmentType === 'review' ? activeAssessment?.user_id : userId,
          reviewerId: assessmentType === 'review' ? userId : null,
          examId: window.sessionStorage.getItem('active-assessment-id'),
          userExamId: window.sessionStorage.getItem('active-assessment-user-id'),
          attempt: 1,
        });
      },
      onMutate: () => {
        toast.loading(`Submitting ${assessmentType === 'review' ? 'Review' : 'Survey'}`);
      },
      onError: () => {
        toast.remove();
        toast.error(`Error Submitting ${assessmentType === 'review' ? 'Review' : 'Survey'}`);
      },
    }
  );

  // Track time per question
  React.useEffect(() => {
    setAnswers((prev) => ({
      ...prev,
      [question.id]: {
        rating: prev[question.id]?.rating ?? null,
        explanation: prev[question.id]?.explanation ?? '',
        duration: prev[question.id]?.duration ?? 0,
        startTime: Date.now(),
      },
    }));
  }, [question.id]);

  const handleRating = (value: number) => {
    setAnswers((prev) => ({
      ...prev,
      [question.id]: {
        ...prev[question.id],
        rating: value,
      },
    }));
    setError('');
  };

  const handleExplanationChange = (e: React.ChangeEvent<HTMLTextAreaElement>) => {
    setAnswers((prev) => ({
      ...prev,
      [question.id]: {
        ...prev[question.id],
        explanation: e.target.value,
      },
    }));
    setError('');
  };

  const validateAnswer = () => {
    const current = answers[question.id];
    if (!current) return false;

    const needsText = question?.type?.includes('free text');
    const needsRating = question?.type?.includes('rating');

    if (needsText && !current.explanation.trim()) return false;
    if (needsRating && current.rating === null) return false;

    return true;
  };

  const saveDuration = () => {
    setAnswers((prev) => {
      const q = prev[question.id];
      const duration = q?.duration ?? 0;
      const startTime = q?.startTime ?? Date.now();
      return {
        ...prev,
        [question.id]: {
          ...q,
          duration: duration + Math.floor((Date.now() - startTime) / 1000),
        },
      };
    });
  };

  const handlePrev = () => {
    saveDuration();
    setCurrentIndex((prev) => Math.max(prev - 1, 0));
    setError('');
  };

  const handleNext = () => {
    if (!validateAnswer()) {
      setError('Please provide the required answer before continuing.');
      return;
    }
    saveDuration();
    setCurrentIndex((prev) => Math.min(prev + 1, questions.length - 1));
  };

  const handleSubmit = () => {
    if (!validateAnswer()) {
      setError('Please provide the required answer before submitting.');
      return;
    }
    saveDuration();

    const payload = {
      time_left: 3560,
      attempt: 1,
      questions: questions.map((q) => {
        const a = answers[q.id] || { rating: null, explanation: '', duration: 0 };
        return {
          question_id: q.id,
          duration: a.duration || 0,
          answers: [
            {
              answer_id: null,
              answer_text: a.explanation,
              rating: a.rating,
              status: true,
            },
          ],
        };
      }),
    };

    updateAnswersMutation.mutate({
      reviewerId: assessmentType === 'review' ? userId : null,
      userId: assessmentType === 'review' ? activeAssessment?.user_id : userId,
      examId: activeAssessment?.exam_id,
      userExamId: window.sessionStorage.getItem('active-assessment-id'),
      data: payload,
    } as any);
  };

  const currentAnswer = answers[question.id] || { rating: null, explanation: '' };

  return (
    <div className='text-lg'>
      <header className='bg-primary flex justify-between px-3 rounded-lg py-4 text-white'>
        <h1 className='text-lg font-semibold'>
          Question {currentIndex + 1} of {questions.length}
        </h1>
        <p className='text-lg mt-1'>Section: {question?.section?.title}</p>
      </header>

      <section className='grid grid-cols-1 gap-0'>
        <div className='md:max-w-[700px]'>
          <h2 className='text-lg mt-5 font-medium'>{question.text}</h2>

          {question?.type?.includes('rating') && (
            <>
              <p className='mt-3'>Rating:</p>
              <div className='flex flex-wrap gap-2 mt-1 mb-4'>
                {Array.from(
                  { length: question.rating_range_to - question.rating_range_from + 1 },
                  (_, i) => {
                    const value = question.rating_range_from + i;
                    return (
                      <button
                        key={value}
                        className={`w-12 h-12 border rounded ${
                          currentAnswer.rating === value
                            ? 'bg-primary text-white border-primary'
                            : 'border-gray-400'
                        }`}
                        onClick={() => handleRating(value)}
                      >
                        {value}
                      </button>
                    );
                  }
                )}
              </div>
            </>
          )}

          {question?.type?.includes('free text') && (
            <>
              <label className='text-lg text-slate-600 mt-3 block font-medium'>
                Explain what it means:
              </label>
              <textarea
                value={currentAnswer.explanation}
                onChange={handleExplanationChange}
                className='w-full h-[200px] border mt-2 focus:outline-none focus:border-primary bg-white border-gray-300 rounded-md p-2'
                placeholder='Write your explanation here...'
              />
            </>
          )}

          {error && <p className='text-red-500 mt-2'>{error}</p>}
        </div>
      </section>

      <section className='flex justify-between md:max-w-[700px]'>
        <button
          disabled={currentIndex === 0}
          className={`border border-primary font-semibold rounded-lg py-3 px-16 mt-5 ${
            currentIndex === 0
              ? 'text-gray-400 border-gray-300 cursor-not-allowed'
              : 'text-primary'
          }`}
          onClick={handlePrev}
        >
          Previous
        </button>

        {currentIndex === questions.length - 1 ? (
          <button
            className='bg-primary rounded-lg text-white py-3 px-16 mt-5'
            onClick={handleSubmit}
          >
            Submit
          </button>
        ) : (
          <button
            className='bg-primary rounded-lg text-white py-3 px-16 mt-5'
            onClick={handleNext}
          >
            Next
          </button>
        )}
      </section>
    </div>
  );
};

export default Employee;
